import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { addItemToCart, addNote, placeOrder, removeItemToCart } from "../../redux/action";

const money = (value) => `₹${Number(value || 0).toFixed(2)}`;

const TableOrderCart = ({ cart = {}, tableName, otp, isOpen, setOpen }) => {
  const dispatch = useDispatch();
  const entries = Object.entries(cart);
  const itemCount = entries.reduce((sum, [, item]) => sum + Number(item.selected || 0), 0);
  const total = entries.reduce(
    (sum, [, item]) => sum + Number(item.price || 0) * Number(item.selected || 0),
    0
  );

  useEffect(() => {
    if (!itemCount && isOpen) setOpen(false);
  }, [isOpen, itemCount, setOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isOpen, setOpen]);

  const changeQuantity = (cartKey, item, direction) => {
    const payload = {
      ...item,
      cartKey,
      tableName,
      foodName: item.name,
    };
    dispatch(direction === "add" ? addItemToCart(payload) : removeItemToCart(payload));
  };

  const handlePlaceOrder = () => {
    if (!entries.length) return;
    const order = entries.map(([, item]) => {
      const addonNames = (item.selectedAddons || [])
        .map((addon) => addon.itemName)
        .filter(Boolean)
        .join(", ");

      return {
        menuId: item.menuId,
        quantity: Number(item.selected),
        price: Number(item.price),
        tableOtp: otp,
        qRScanLogId: null,
        sizeOptionId: item.selectedSize?.sizeOptionId || null,
        addonItemIds: (item.selectedAddons || []).map((addon) => addon.addonItemId),
        addonSummary: addonNames ? `Add-ons: ${addonNames}` : null,
        note: item.note || null,
      };
    });
    dispatch(placeOrder(order, tableName));
  };

  return (
    <>
      <button
        type="button"
        className={`table-order-backdrop${isOpen ? " is-open" : ""}`}
        aria-label="Close order cart"
        onClick={() => setOpen(false)}
      />

      <aside className={`table-order-cart${isOpen ? " is-open" : ""}`} aria-label="Current order">
        <header className="table-order-cart-header">
          <div>
            <h3>Current order</h3>
            <span>{itemCount} {itemCount === 1 ? "item" : "items"} selected</span>
          </div>
          <button type="button" className="table-order-cart-close" aria-label="Close current order" onClick={() => setOpen(false)}>×</button>
        </header>

        <div className="table-order-cart-lines">
          {!entries.length && (
            <div className="table-order-cart-empty">
              <strong>Your order is empty</strong>
              <span>Add a food item to see it here.</span>
            </div>
          )}

          {entries.map(([cartKey, item]) => (
            <article className="table-order-cart-line" key={cartKey}>
              <div className="table-order-cart-copy">
                <strong>{item.name || "Food item"}</strong>
                {item.selectedSize?.sizeName && <span>Size: {item.selectedSize.sizeName}</span>}
                {(item.selectedAddons || []).length > 0 && (
                  <div className="table-order-addon-list">
                    {(item.selectedAddons || []).map((addon) => (
                      <span key={addon.addonItemId || addon.itemName}>
                        + {addon.itemName}{Number(addon.price) ? ` (${money(addon.price)})` : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <strong className="table-order-line-total">{money(Number(item.price) * Number(item.selected))}</strong>

              <div className="table-order-quantity" aria-label={`Quantity for ${item.name || "food item"}`}>
                <button type="button" className="table-order-quantity-button" aria-label={`Decrease ${item.name || "food item"}`} onClick={() => changeQuantity(cartKey, item, "remove")}>−</button>
                <span>{item.selected}</span>
                <button type="button" className="table-order-quantity-button" aria-label={`Increase ${item.name || "food item"}`} onClick={() => changeQuantity(cartKey, item, "add")}>+</button>
              </div>

              <label className="table-order-note">
                <input
                  className="table-order-note-input"
                  aria-label="Item note"
                  value={item.note || ""}
                  maxLength="1000"
                  placeholder="Add item note: e.g. less spicy"
                  onChange={(event) => dispatch(addNote({ tableName, id: cartKey, note: event.target.value }))}
                />
              </label>
            </article>
          ))}
        </div>

        <footer className="table-order-cart-footer">
          <div><span>Subtotal</span><strong>{money(total)}</strong></div>
          <button type="button" disabled={!entries.length} onClick={handlePlaceOrder}>Place Order</button>
        </footer>
      </aside>

      {itemCount > 0 && (
        <div className="table-order-mobile-bar">
          <div><strong>{itemCount} {itemCount === 1 ? "item" : "items"}</strong><span>{money(total)}</span></div>
          <button type="button" onClick={() => setOpen(true)}>View order</button>
        </div>
      )}
    </>
  );
};

export default TableOrderCart;
